<template>
  <div v-if="questionCount <= 3" class="questions pt-3 pt-lg-6 pb-3">
    <section class="text-center mb-3">
      <h1 class="heading mb-3">Find Your Travel Personality. <br><span class="font-aldine-italic">And Let the Adventures Begin.</span></h1>
      <p class="mxw-sm">Thanks for being a Marriott Bonvoy<sup>®</sup> Credit Card Member. Take the quiz below to discover your travel personality and get tips, inspiration, and more to make your next trip even more rewarding.</p>
    </section>
    <section>
      <div class="text-center">
        <p class="question-count mb-2" ref="questionEl" tabindex="0">Question {{ questionCount }} of 3</p>
        <h2 class="font-aldine-italic fz-xl mt-0-5 mb-2" v-html="content.questions[questionCount - 1]"></h2>
      </div>
      <div class="wrap">
        <div class="module-1">
          <img :src="content.question[questionCount - 1][0].img_src" alt="">
          <div class="content-wrap">
            <label v-html="content.question[questionCount - 1][0].copy" id="label-a1" for="a1"></label>
            <input type="radio" name="answers" id="a1" @input="onInput(1)" ref="inputAnswer1" :tracking-id="content.question[questionCount - 1][0].trackingId" aria-labelledby="label-a1">
          </div>
        </div>
        <div class="module-2">
          <img :src="content.question[questionCount - 1][1].img_src" alt="">
          <div class="content-wrap">
            <label v-html="content.question[questionCount - 1][1].copy" id="label-a2" for="a2"></label>
            <input type="radio" name="answers" id="a2" @input="onInput(2)" ref="inputAnswer2" :tracking-id="content.question[questionCount - 1][1].trackingId" aria-labelledby="label-a2">
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center mt-3">
        <div :class="`${selectedAnswerId === 0 ? 'not-allowed' : ''}`">
          <a v-if="questionCount < 3" role="button" :class="`btn-1 ${selectedAnswerId === 0 ? 'disabled' : ''}`" @click="onNext" @keydown.enter="onNext">Next</a>
          <a v-else role="button" :class="`btn-1 ${selectedAnswerId === 0 ? 'disabled custom_click_track' : 'custom_click_track'}`" @click="onSubmit" @keydown.enter="onSubmit" :custom_click_track_value="trackingValue">See Your Result</a>
        </div>
      </div>
    </section>
  </div>

  <div v-else :class="`result pt-${resultTopPadMob} pt-lg-${resultTopPadDes} pb-3`">
    <section class="text-center">
      <img :src="content.result[resultIndex].icon_src" :style="`width: ${content.result[resultIndex].icon_width}px; height: ${content.result[resultIndex].icon_height}px;`" alt="" class="icon">
      <h1 :class="`heading ${resultIndex === 1 ? '' : 'mt-1'} mb-3`" v-html="content.result[resultIndex].heading"></h1>
      <p class="mxw-md px-sm-2" v-html="content.result[resultIndex].body"></p>
    </section>
    <section class="flex-grid mt-4">
      <div v-for="(result, key) in content.result[resultIndex].modules" class="d-flex flex-column align-items-center">
        <h2 class="label mb-3">{{ content.result[resultIndex][`label_${key + 1}`] }}</h2>
        <a :href="result.cta_href" class="module d-flex flex-column flex-auto custom_click_track" :custom_click_track_value="trackingValue" target="_blank">
          <div class="img-wrap">
            <img :src="result.img_src" :alt="result.img_alt">
            <div v-if="result.logo_src" class="logo-wrap">
              <img :src="result.logo_src" :style="`width: ${result.logo_width}px; height: ${result.logo_height}px;`" :alt="result.logo_alt">
            </div>
          </div>
          <div class="wrap d-flex flex-column flex-auto align-items-center text-center">
            <h3 class="heading fz-md mb-2" v-html="result.heading"></h3>
            <p v-html="result.body" class="mb-2"></p>
            <a :href="result.cta_href" class="link-arrow mt-auto custom_click_track" :custom_click_track_value="trackingValue" target="_blank">{{ result.cta_copy }}</a>
          </div>
        </a>
      </div>
      <div class="d-flex justify-content-center w-100 mt-2 mt-lg-5 mr-0">
        <a role="button" class="btn-1 custom_click_track" @click="resetQuestions" @keydown.enter="resetQuestions" :custom_click_track_value="trackingValuePlayAgain">Play Again</a>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { compareArrays } from '../utilities';

compareArrays();

const content = APP_content.deck_of_cards;
const questionCount = ref(1);
const answersArray = ref([]);
const selectedAnswerId = ref(0);
const trackingQ1 = ref('');
const trackingQ2 = ref('');
const trackingQ3 = ref('');
const inputAnswer1 = ref(null);
const inputAnswer2 = ref(null);
const resultIndex = ref(0);
const resultTopPadDes = ref(6);
const resultTopPadMob = ref(3);
const questionEl = ref(null);

const trackingValue = computed(() => {
  return `CMA-Game|${trackingQ1.value}${trackingQ2.value}${trackingQ3.value}|external`;
});

const trackingValuePlayAgain = computed(() => {
  return `CMA-QuizResults|PlayAgain${trackingQ1.value}${trackingQ2.value}${trackingQ3.value}|external`;
});

function switchAnswer(answerId, trackingRef) {
  if (answerId === 1) {
    inputAnswer1.value.checked = true;
    trackingRef.value = inputAnswer1.value.getAttribute('tracking-id');
  } else if (answerId === 2) {
    inputAnswer2.value.checked = true;
    trackingRef.value = inputAnswer2.value.getAttribute('tracking-id');
  }
}

function onInput(answerId) {
  selectedAnswerId.value = answerId;
  console.log(answerId);
  if (questionCount.value === 1) {
    switchAnswer(answerId, trackingQ1);
  } else if (questionCount.value === 2) {
    switchAnswer(answerId, trackingQ2);
  } else if (questionCount.value === 3) {
    switchAnswer(answerId, trackingQ3);
  }
}

function onNext() {
  questionCount.value++;
  answersArray.value.push(selectedAnswerId.value);
  inputAnswer1.value.checked = false;
  inputAnswer2.value.checked = false;
  selectedAnswerId.value = 0;
  questionEl.value.focus();
}

function onSubmit() { // "SEE YOUR RESULT" Button
  window.scrollTo(0, 0);
  answersArray.value.push(selectedAnswerId.value);
  inputAnswer1.value.checked = false;
  inputAnswer2.value.checked = false;
  selectedAnswerId.value = 0;
  questionCount.value++;

  const _answersArray = answersArray._rawValue;
  switch (true) {
    case _answersArray.equals([1, 1, 2]):
      console.log([1, 1, 2]);
      resultIndex.value = 0;
      break;
    case _answersArray.equals([1, 1, 1]):
      console.log([1, 1, 1]);
      resultIndex.value = 1;
      break;
    case _answersArray.equals([1, 2, 2]):
      console.log([1, 2, 2]);
      resultIndex.value = 2;
      break;
    case _answersArray.equals([1, 2, 1]):
      console.log([1, 2, 1]);
      resultIndex.value = 3;
      break;
    case _answersArray.equals([2, 1, 2]):
      console.log([2, 1, 2]);
      resultIndex.value = 4;
      break;
    case _answersArray.equals([2, 1, 1]):
      console.log([2, 1, 1]);
      resultIndex.value = 5;
      break;
    case _answersArray.equals([2, 2, 2]):
      console.log([2, 2, 2]);
      resultIndex.value = 6;
      break;
    case _answersArray.equals([2, 2, 1]):
      console.log([2, 2, 1]);
      resultIndex.value = 7;
      resultTopPadDes.value = 3;
      resultTopPadMob.value = 0;
      break;
    default:
      console.error('Error with answersArray');
  }
}

function resetQuestions() {
  questionCount.value = 1;
  answersArray.value = [];
  window.scrollTo(0, 0);
}
</script>
